import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';

import { graphql } from '@/gql/graphql';

const GetBadgeDelete = graphql(/* GraphQL */ `
  query GetBadgeDelete($badgeId: uuid!) {
    badge_by_pk(id: $badgeId) {
      id
      title
    }
  }
`);

const DeleteBadge = graphql(/* GraphQL */ `
  mutation InsertBadge($badgeId: uuid!) {
    delete_badge_by_pk(id: $badgeId) {
      id
    }
  }
`);

export default function BadgeDelete() {
  const { back } = useRouter();
  const { badgeId } = useLocalSearchParams<{
    badgeId?: string;
  }>();

  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetBadgeDelete,
    variables: {
      badgeId: badgeId!,
    },
  });

  const [, deleteBadge] = useMutation(DeleteBadge);

  const badge = data?.badge_by_pk;

  const onDelete = async (badgeId: string) => {
    const result = await deleteBadge({ badgeId });
    if (result.error || !result.data?.delete_badge_by_pk)
      return { success: false };
    return { success: true };
  };

  return (
    <>
      <Stack.Screen
        options={{ title: badge?.title ? `Delete ${badge.title}` : 'Delete' }}
      />
      <ContentBoundary
        data={badge?.title ? { id: badge.id, title: badge.title } : null}
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={<EmptyPlaceholder message="Deletion is unavailable." />}
      >
        {(badge) => (
          <DestructiveAction
            key={badge.id}
            confirmationName={badge.title}
            label="Delete badge"
            submittingLabel="Deleting…"
            onAction={() => onDelete(badge.id)}
            onSuccess={() => {
              back();
              back();
              back();
            }}
          >
            <DestructiveActionHeading>
              Are you sure you want to delete {badge.title}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This cannot be undone. It will remove all assignments for this
              badge as well.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
